import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Sparkles, ChevronRight, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HeroSection = ({ onScrollToProducts }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.first_name) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source: 'hero_form'
      });
      setSubmitted(true);
      toast.success('Welcome to Winamor! Check your email for exclusive offers.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="hero-bg relative min-h-screen flex items-center" style={{
      backgroundImage: `url('https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/v4ikmef7_MDM09428.jpg')`
    }}>
      <div className="hero-overlay" />
      
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-purple-600/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-fuchsia-600/10 rounded-full blur-3xl" />
      
      <div className="relative z-10 container-custom w-full py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content - SEO Optimized */}
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/40 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium text-purple-200">Australia's Best Keratin Iron</span>
            </div>
            
            {/* H1 - SEO Optimized */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-white">The </span>
              <span className="text-gradient italic">Best Straightener</span>
              <br />
              <span className="text-white">for </span>
              <span className="text-yellow-400 italic">Keratin</span>
              <span className="text-white"> Treatments</span>
            </h1>
            
            {/* SEO Rich Description */}
            <p className="text-lg text-purple-200 mb-8 max-w-xl leading-relaxed">
              <strong className="text-white">The Winamor Perfect Pass counts your passes for you.</strong> Australia's first keratin iron 
              with built-in pass counting technology. No more guessing, no more over-processing. 
              Perfect keratin results every single time.
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="text-sm text-purple-200">Pass Counter LCD Display</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="text-sm text-purple-200">Vibration Alerts</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="text-sm text-purple-200">70-230°C Range</span>
              </div>
            </div>
            
            <Button 
              onClick={onScrollToProducts}
              data-testid="hero-shop-btn"
              className="btn-primary text-lg px-8 py-6 flex items-center gap-2 group"
            >
              Shop Keratin Iron - $310
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          
          {/* Right: Lead Form */}
          <div className="animate-fade-in-up stagger-2">
            <div className="form-card max-w-md mx-auto lg:ml-auto">
              {!submitted ? (
                <>
                  <h3 className="text-2xl font-bold text-white mb-2">Get Early Access</h3>
                  <p className="text-purple-300 mb-6">Join the waitlist for exclusive offers on Australia's best keratin straightener.</p>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Input
                        type="text"
                        placeholder="First Name"
                        data-testid="hero-first-name-input"
                        className="input-glass w-full"
                        value={formData.first_name}
                        onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Email Address"
                        data-testid="hero-email-input"
                        className="input-glass w-full"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Input
                        type="tel"
                        placeholder="Phone (Optional)"
                        data-testid="hero-phone-input"
                        className="input-glass w-full"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                    
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="hero-submit-btn"
                      className="btn-primary w-full py-4 text-lg"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Joining...
                        </span>
                      ) : (
                        'Get Early Access'
                      )}
                    </Button>
                  </form>
                  
                  <p className="text-xs text-purple-400 mt-4 text-center">
                    No spam. Unsubscribe anytime.
                  </p>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="checkmark mx-auto mb-4">
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">You're In!</h3>
                  <p className="text-purple-300">Check your email for exclusive early access offers.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
